import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared primitives for "type in a box, ask the server, show the answer".
 *
 * WHY THIS EXISTS. Five surfaces had rolled their own version of this and no
 * two agreed: Gag Breakdown debounced at 300ms and guarded stale responses with
 * a `fetchSeq` ref; UserAutocomplete and HazardAutocomplete debounced at 200ms
 * and guarded nothing; MarketplaceBrowse debounced at 300ms and guarded
 * nothing; RiskAssessmentOverview did not debounce at all and fired a request
 * per keystroke. Only the Gag pages were correct, so the shape was already
 * known in this codebase — it just had not been shared. Per CLAUDE.md's Code
 * Unification Principle, this module is that shape extracted, and new search
 * surfaces must import it rather than hand-rolling a sixth variant.
 *
 * THE BUG IT CLOSES (measured, not argued —
 * `components/UserAutocomplete.staleResponse.test.js`). A search box that fires
 * a request per (debounced) keystroke has more than one request in flight the
 * moment the user pauses and resumes typing, which is what typing a real word
 * looks like. Responses are not ordered. A longer query usually matches fewer
 * rows and so comes back SOONER, meaning the stale answer to the shorter prefix
 * routinely lands LAST and overwrites the fresh list. The user sees a box
 * reading "flambe" above the results for "fla".
 *
 * It is invisible on a fast local machine and ordinary on a laggy connection,
 * and it explains BOTH halves of the report it was found from — "the search
 * doesn't always seem to work correctly" and "doesn't always update as we type
 * letters in" are one defect, not two.
 */

/**
 * The last write wins — and "last" means the most recently STARTED request, not
 * the most recently finished one.
 *
 * Returns a `begin()` you call as a request starts. `begin()` hands back an
 * `isCurrent()` predicate; check it before every `setState` that applies the
 * result, including in `catch` and `finally`.
 *
 *   const begin = useLatestRequest();
 *   const isCurrent = begin();
 *   const data = await fetchIt(q);
 *   if (!isCurrent()) return;      // a newer request has since started
 *   setRows(data);
 *
 * Guarding the FAILURE path matters just as much as the success path: a stale
 * rejection that paints an error banner, or a stale `finally` that clears the
 * spinner, is the same defect wearing a different hat. UserAutocomplete showed
 * a false "No match for …" for exactly that reason.
 *
 * A counter rather than an `AbortController` on purpose. Aborting cancels the
 * HTTP request, which is a nice-to-have; it does NOT by itself stop an
 * already-resolved older response from being applied, and `apiFetch`
 * (utils/api.js) owns the request options, so threading a signal through every
 * caller would be a wider change for a weaker guarantee. `AbortController`
 * remains correct where it is already used (DepartmentContext, RaSummaryCard) —
 * this is for the search boxes.
 */
export function useLatestRequest() {
  const seq = useRef(0);
  return useCallback(() => {
    const mine = ++seq.current;
    return () => mine === seq.current;
  }, []);
}

/**
 * `value`, but it stops changing until the user stops typing for `delay` ms.
 *
 * Debouncing is a request-volume control, NOT a correctness control — it
 * narrows the window in which two requests overlap without ever closing it.
 * Anything that debounces a fetch still needs `useLatestRequest`. Treating the
 * debounce as the fix is precisely why four of the five surfaces above looked
 * safe and were not.
 *
 * 250ms by default: the three sites that debounced at all used 200ms or 300ms
 * with no recorded reason for either, so this is one number rather than two.
 * The parameter stays because a local-array filter wants a shorter one than a
 * round trip.
 */
export function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    // No timer at all when the value already agrees. Without this an unrelated
    // re-render schedules a pointless state update, and callers that key a
    // fetch off the debounced value refetch for nothing.
    if (value === debounced) return undefined;
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay, debounced]);

  return debounced;
}
